import { useMemo, useState } from 'react';
import { Pagination } from '../../../components/shared';
import { useI18n } from '../../../i18n';
import { cn } from '../../../utils/cn';
import { useMoney } from '../../../providers/CurrencyProvider';

export interface BestSellingProduct {
  id: string;
  name: string;
  imageUrl?: string;
  sold: number;
  revenue: number;
  /** API top-products chưa trả xu hướng — bỏ trống thì không render badge %. */
  trend?: number;
}

interface BestSellingTableProps {
  products: BestSellingProduct[];
  isLoading?: boolean;
  onViewAll?: () => void;
  pageSize?: number;
  className?: string;
}

// `sold` là số lượng bán, không phải tiền -> không format kèm ký hiệu tiền tệ.
function formatSold(value: number): string {
  if (value >= 1000) return `${(value / 1000).toFixed(1)}k`;
  return `${value}`;
}

export function BestSellingTable({
  products,
  isLoading = false,
  onViewAll,
  pageSize = 5,
  className,
}: BestSellingTableProps) {
  const { t } = useI18n();
  const money = useMoney();
  const [currentPage, setCurrentPage] = useState(1);

  // Helper nằm trong component vì `useMoney()` là hook.
  const formatRevenue = (value: number): string => {
    if (isNaN(value) || value === 0) return money.format(0, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    if (value >= 1000) return money.compact(value);
    return money.format(value, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  const totalPages = Math.max(Math.ceil(products.length / pageSize), 1);
  // Dữ liệu đổi (đổi store / đổi khoảng ngày) có thể làm trang hiện tại vượt range.
  const safePage = Math.min(currentPage, totalPages);
  const paginated = useMemo(
    () => products.slice((safePage - 1) * pageSize, safePage * pageSize),
    [products, safePage, pageSize]
  );

  if (isLoading) {
    return <div className={cn('h-[300px] animate-pulse rounded-xl bg-slate-200', className)} />;
  }

  return (
    <div
      className={cn('rounded-xl bg-white shadow-sm', className)}
      style={{ boxShadow: '0px 1px 2px rgba(0,0,0,0.05)' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4">
        <h3 className="font-sans text-base font-bold text-slate-900">{t('bestSellingProducts')}</h3>
        {onViewAll && (
          <button
            onClick={onViewAll}
            className="text-xs font-semibold uppercase tracking-wide text-slate-400 hover:text-slate-600"
          >
            {/* Bug #24: nút điều hướng sang trang Products nên nhãn phải là
                "All Products", không phải "This month" gây hiểu nhầm là filter. */}
            {t('allProducts')}
          </button>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-y border-slate-100 bg-slate-50/60">
              <th className="whitespace-nowrap px-6 py-2.5 text-left text-[11px] font-bold uppercase tracking-[0.55px] text-slate-400">{t('dashboardProduct')}</th>
              <th className="whitespace-nowrap px-6 py-2.5 text-right text-[11px] font-bold uppercase tracking-[0.55px] text-slate-400">{t('sold')}</th>
              <th className="whitespace-nowrap px-6 py-2.5 text-right text-[11px] font-bold uppercase tracking-[0.55px] text-slate-400">{t('revenue')}</th>
            </tr>
          </thead>
          <tbody>
            {products.length === 0 ? (
              <tr>
                <td colSpan={3} className="px-6 py-10 text-center text-sm text-slate-400">
                  {t('noProductsFound')}
                </td>
              </tr>
            ) : (
              paginated.map((product) => (
                <tr key={product.id} className="border-b border-slate-100 last:border-0">
                  <td className="px-6 py-3">
                    <div className="flex items-center gap-3">
                      {product.imageUrl ? (
                        <img
                          src={product.imageUrl}
                          alt={product.name}
                          className="h-10 w-10 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100 text-slate-400">
                          <span className="text-xs font-medium">{(product.name ?? '?').charAt(0)}</span>
                        </div>
                      )}
                      <span className="text-sm font-medium text-slate-900">{product.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-3 text-right">
                    <span className="text-sm text-slate-500">{formatSold(product.sold)}</span>
                  </td>
                  <td className="px-6 py-3 text-right">
                    <div className="flex flex-col items-end">
                      <span className="text-sm font-semibold text-slate-900">{formatRevenue(product.revenue)}</span>
                      {product.trend != null && product.trend !== 0 && (
                        <span
                          className={cn(
                            'text-xs font-medium',
                            product.trend > 0 ? 'text-[#1FBE63]' : 'text-[#E8472A]'
                          )}
                        >
                          {product.trend > 0 ? '+' : ''}{product.trend}%
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {paginated.length > 0 && (
        <Pagination
          variant="numbered"
          currentPage={safePage}
          totalPages={totalPages}
          totalItems={products.length}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          itemLabel={t('products')}
          className="border-t border-slate-100"
        />
      )}
    </div>
  );
}
